import * as React from "react";
import {
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Grid,
  InputLabel,
  Link,
  MenuItem,
  Modal as MuiModal,
  TextField,
} from "@mui/material";
import { ClearIcon } from "@mui/x-date-pickers/icons";
import moment from "moment";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { AuthenticationContext, ImagesContext } from "../Context";
import { getHeaders } from "../utils/HeaderHelper";
import { useFlash } from "../FlashContext";

export interface SupplyItem {
  name: string;
  min_quantity: number;
  max_quantity: number | null;
}

interface AddSupplyRequestModalProps {
  open: boolean;
  onClose: () => void;
  encounterId: string;
  payerNames: string[];
  availableItems: string[];
  availableItemObjects?: SupplyItem[];
  onSaveSuccess: () => void;
  isEdit?: boolean;
  existingData?: any;
}

// constants
const TRACKING_NUMBER_MAX_LENGTH = 100;
const DESCRIPTION_MAX_LENGTH = 500;

const todayIsoDate = (): string => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const AddSupplyRequestModal: React.FC<AddSupplyRequestModalProps> = ({
  open,
  onClose,
  encounterId,
  payerNames,
  availableItems,
  availableItemObjects = [],
  onSaveSuccess,
  isEdit = false,
  existingData = null,
}) => {
  const authenticationSetting = React.useContext(AuthenticationContext);
  const images = React.useContext<any>(ImagesContext);
  const { showToast } = useFlash();

  // Form state
  const [requestDate, setRequestDate] = React.useState<string>(todayIsoDate());
  const [supplyItem, setSupplyItem] = React.useState<string>("");
  const [quantity, setQuantity] = React.useState<string>("");
  const [trackingNumber, setTrackingNumber] = React.useState<string>("");
  const [description, setDescription] = React.useState<string>("");
  const [loading, setLoading] = React.useState<boolean>(false);
  const [openRequestDate, setOpenRequestDate] = React.useState<boolean>(false);

  const computedItems = React.useMemo(() => {
    if (!supplyItem) return availableItems;
    if (!availableItems.includes(supplyItem)) {
      return [supplyItem, ...availableItems];
    }
    return availableItems;
  }, [availableItems, supplyItem]);

  const selectedItemLimits = React.useMemo(
    () => availableItemObjects.find((item) => item.name === supplyItem) ?? null,
    [availableItemObjects, supplyItem],
  );

  // Reset every time the modal opens
 React.useEffect(() => {
  if (open) {
    if (isEdit && existingData) {
      setRequestDate(existingData.request_date || todayIsoDate());
      setSupplyItem(existingData.supply_request_item || "");
      setQuantity(existingData.item_quantity?.toString() || "");
      setTrackingNumber(existingData.tracking_number || "");
      setDescription(existingData.description || "");
    } else {
      // Add mode reset
      setRequestDate(todayIsoDate());
      setSupplyItem("");
      setQuantity("");
      setTrackingNumber("");
      setDescription("");
    }

    setLoading(false);
  }
}, [open, isEdit, existingData]);

  const handleClose = () => {
    if (loading) return;
    onClose();
  };

  const handleSubmit = async () => {
  if (!requestDate) {
    showToast({
      title: "Request Date Required",
      text: "Please select a Request Date.",
      type: "error",
    });
    return;
  }

  if (!supplyItem) {
    showToast({
      title: "Supply Request Item Required",
      text: "Please select a Supply Request Item.",
      type: "error",
    });
    return;
  }

    const parsedQuantity = Number(quantity);
    if (!quantity || Number.isNaN(parsedQuantity) || parsedQuantity <= 0) {
      showToast({
        title: "Item Quantity Required",
        text: "Please enter a valid Item Quantity greater than zero.",
        type: "error",
      });
      return;
    }

    if (selectedItemLimits) {
      const { name, min_quantity, max_quantity } = selectedItemLimits;
      if (
        parsedQuantity < min_quantity ||
        (max_quantity !== null && parsedQuantity > max_quantity)
      ) {
        const rangeText =
          max_quantity !== null
            ? `between ${min_quantity} and ${max_quantity}`
            : `at least ${min_quantity}`;
        showToast({
          title: "Invalid Quantity",
          text: `Quantity for ${name} must be ${rangeText}.`,
          type: "error",
        });
        return;
      }
    }

    setLoading(true);

    const payload = {
      supply_request: {
        encounter_billing_id: encounterId,
        request_date: requestDate,
        supply_request_item: supplyItem,
        item_quantity: parsedQuantity,
        tracking_number: trackingNumber.trim() || null,
        description: description.trim() || null,
      },
    };

    try {
      const url = isEdit
        ? `/data_fetching/supply_requests/${existingData.id}.json`
        : "/data_fetching/supply_requests.json";

      const method = isEdit ? "PATCH" : "POST";

      const response = await fetch(url, {
        method,
        headers: getHeaders(authenticationSetting.csrfToken),
        body: JSON.stringify(payload),
      });

      const result = await response.json();

      if (result?.success) {
        showToast({
          title: isEdit
            ? "Supply Request Updated"
            : "Supply Request Saved",
          text: isEdit
            ? "Supply request updated successfully."
            : "Supply request created successfully.",
          type: "success",
        });

        onSaveSuccess(); // refresh table
        onClose();
      } else {
        showToast({
          title: isEdit
            ? "Error Updating Supply Request"
            : "Error Saving Supply Request",
          text: result?.message || "Something went wrong.",
          type: "error",
        });
      }
    } catch (error: any) {
      showToast({
        title: isEdit
          ? "Error Updating Supply Request"
          : "Error Saving Supply Request",
        text: error?.message ?? "An unexpected error occurred.",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <MuiModal open={open} className="modal-primary" onClose={handleClose}>
      <div 
        className="paper" 
        style={{ width: 430, maxHeight: "90vh", display: "flex", flexDirection: "column" }}
      >
        <div 
          className="paperInner" 
          style={{ display: "flex", flexDirection: "column", flexGrow: 1, overflow: "hidden" }}
        >
          <Grid container sx={{ flexGrow: 1, display: "flex", flexDirection: "column", flexWrap: "nowrap", overflow: "hidden" }}>
            
            {/* --- HEADER (Fixed at top) --- */}
            <Grid item xs={12} sx={{ flexShrink: 0 }}>
              <div className="main-header" style={{ marginBottom: 14 }}>
               <span>{isEdit ? "Edit Supply Request" : "Add Supply Request"}</span>
                <span id="dismiss-button" onClick={handleClose}>
                  <ClearIcon sx={{ width: 30, height: 30 }} />
                </span>
              </div>
            </Grid>

            {/* --- FORM FIELDS --- */}
            <Grid
              item
              xs={12}
              sx={{
                paddingLeft: "24px",
                paddingRight: "24px",
                paddingBottom: "24px", 
                display: "flex",
                flexDirection: "column",
                rowGap: "20px",
                overflowY: "auto", 
                flexGrow: 1,       
              }}
            >
              {/* Helper text with payer(s) */}
              <Grid container>
                <Grid item xs={12}>
                  <p
                    style={{
                      color: "#000",
                      fontSize: 13,
                      fontWeight: 400,
                      lineHeight: "18px",
                      margin: 0,
                    }}
                  >
                    This supply request is associated with a patient with the
                    following payer{payerNames.length > 1 ? "s" : ""}:{" "}
                    {payerNames.length > 0 ? (
                      payerNames.map((name, index) => (
                        <React.Fragment key={name}>
                          <strong style={{ fontFamily: "QuicksandBold" }}>{name}</strong>
                          {index < payerNames.length - 1 ? ", " : ""}
                        </React.Fragment>
                      ))
                    ) : (
                      <strong style={{ fontFamily: "QuicksandBold" }}>N/A</strong>
                    )}
                    . Available supply items are determined by this payer's coverage rules.
                  </p>
                </Grid>
              </Grid>

              {/* Request Date */}
              <Grid container>
                <Grid item xs={12}>
                  <InputLabel htmlFor="request_date" className="field-label">
                    Request Date*
                  </InputLabel>
                </Grid>
                <Grid item xs={12} className="field-container">
                  <Box
                    sx={{
                      width: "100%",
                      boxSizing: "border-box",
                      backgroundColor: "#efe9e7",
                      display: "flex",
                      borderRadius: "5px",
                      alignItems: "center",
                      paddingRight: "8px",
                    }}
                  >
                    <LocalizationProvider dateAdapter={AdapterMoment}>
                      <DatePicker
                        open={openRequestDate}
                        onOpen={() => !loading && setOpenRequestDate(true)}
                        onClose={() => setOpenRequestDate(false)}
                        value={requestDate ? moment(requestDate) : null}
                        onChange={(date: moment.Moment | null) => {
                          setRequestDate(
                            date ? date.format("YYYY-MM-DD") : "",
                          );
                        }}
                        format="MMMM Do, YYYY"
                        disableOpenPicker
                        disabled={loading}
                        slotProps={{
                          textField: {
                            size: "small",
                            placeholder: "Select Date",
                            sx: {
                              flex: 1,
                              "& fieldset": {
                                border: "none",
                                outline: "none",
                              },
                              "& input": {
                                fontSize: "14px",
                              },
                              "& input::placeholder": {
                                color: "#A29D9B",
                                opacity: 1,
                                fontSize: "14px",
                              },
                            },
                          },
                        }}
                      />
                    </LocalizationProvider>
                    <Link
                      className="invite-app-user-link"
                      onClick={() => !loading && setOpenRequestDate(true)}
                      sx={{
                        cursor: loading ? "not-allowed" : "pointer",
                        opacity: loading ? 0.5 : 1,
                        display: "flex",
                        alignItems: "center",
                        marginLeft: "4px",
                      }}
                    >
                      <img
                        src={images?.calendar_icon}
                        alt="Calendar"
                        className="action-icon-image"
                        style={{ width: 22.75, maxHeight: 26 }}
                      />
                    </Link>
                  </Box>
                </Grid>
              </Grid>

              {/* Supply Request Item */}
              <Grid container>
                <Grid item xs={12}>
                  <InputLabel htmlFor="supply_item" className="field-label">
                    Supply Request Item*
                  </InputLabel>
                </Grid>
                <Grid item xs={12} className="field-container">
                  <TextField
                    id="supply_item"
                    select
                    size="small"
                    value={supplyItem}
                    className="the-field"
                    required
                    variant="outlined"
                    disabled={loading}
                    onChange={(
                      event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
                    ) => setSupplyItem(event.target.value)}
                    SelectProps={{
                      displayEmpty: true,
                      renderValue: (selected: unknown) => {
                        if (!selected) {
                          return (
                            <span style={{ color: "#A29D9B", opacity: 1 }}>
                              Select Item
                            </span>
                          );
                        }
                        return selected as string;
                      },
                    }}
                    sx={{
                      width: "100% !important",
                      backgroundColor: "#efe9e7 !important",
                      borderRadius: "5px",
                      "& fieldset": {
                        border: "none",
                        outline: "none",
                      },
                    }}
                  >
                    {computedItems.length === 0 ? (
                      <MenuItem value="" disabled>
                        No items available
                      </MenuItem>
                    ) : (
                      computedItems.map((item) => (
                        <MenuItem key={item} value={item}>
                          {item}
                        </MenuItem>
                      ))
                    )}
                  </TextField>
                </Grid>
              </Grid>

              {/* Item Quantity */}
              <Grid container>
                <Grid item xs={12}>
                  <InputLabel htmlFor="item_quantity" className="field-label">
                    Item Quantity*
                  </InputLabel>
                </Grid>
                <Grid item xs={12} className="field-container">
                  <TextField
                    id="item_quantity"
                    type="number"
                    size="small"
                    value={quantity}
                    className="the-field"
                    required
                    variant="outlined"
                    placeholder="Enter Quantity"
                    disabled={loading}
                    onChange={(
                      event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
                    ) => {
                      const { value } = event.target;
                      // Only allow positive integers (or empty for clearing the field)
                      if (value === "" || /^\d+$/.test(value)) {
                        setQuantity(value);
                      }
                    }}
                    inputProps={{
                      min: selectedItemLimits?.min_quantity ?? 1,
                      ...(selectedItemLimits?.max_quantity != null && {
                        max: selectedItemLimits.max_quantity,
                      }),
                      inputMode: "numeric",
                    }}
                    sx={{
                      width: "100% !important",
                      backgroundColor: "#efe9e7 !important",
                      borderRadius: "5px",
                      "& fieldset": {
                        border: "none",
                        outline: "none",
                      },
                      "& input::placeholder": {
                        color: "#A29D9B",
                        opacity: 1,
                      },
                    }}
                  />
                </Grid>
              </Grid>

              {/* Tracking Number */}
              <Grid container>
                <Grid item xs={12}>
                  <InputLabel htmlFor="tracking_number" className="field-label">
                    Tracking Number
                  </InputLabel>
                </Grid>
                <Grid item xs={12} className="field-container">
                  <TextField
                    id="tracking_number"
                    size="small"
                    value={trackingNumber}
                    className="the-field"
                    variant="outlined"
                    placeholder="Number (optional)"
                    disabled={loading}
                    error={trackingNumber.length > TRACKING_NUMBER_MAX_LENGTH}
                    helperText={
                      trackingNumber.length > TRACKING_NUMBER_MAX_LENGTH
                        ? `Maximum length is ${TRACKING_NUMBER_MAX_LENGTH} characters.`
                        : ""
                    }
                    onChange={(
                      event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
                    ) => {
                      const { value } = event.target;
                      if (value.length > TRACKING_NUMBER_MAX_LENGTH) return;
                      setTrackingNumber(value);
                    }}
                    inputProps={{ maxLength: TRACKING_NUMBER_MAX_LENGTH }}
                    sx={{
                      width: "100% !important",
                      backgroundColor: "#efe9e7 !important",
                      borderRadius: "5px",
                      "& fieldset": {
                        border: "none",
                        outline: "none",
                      },
                      "& input::placeholder": {
                        color: "#A29D9B",
                        opacity: 1,
                        fontSize: "16px",
                        fontWeight: 400,
                      },
                      "& input": {
                        fontSize: "16px",
                        fontWeight: 500,
                      },
                    }}
                  />
                </Grid>
              </Grid>

              {/* Description */}
              <Grid container>
                <Grid item xs={12}>
                  <InputLabel htmlFor="description" className="field-label">
                    Description
                  </InputLabel>
                </Grid>
                <Grid item xs={12} className="field-container">
                  <TextField
                    id="description"
                    size="small"
                    value={description}
                    className="the-field"
                    variant="outlined"
                    placeholder="Description (optional)"
                    disabled={loading}
                    error={description.length > DESCRIPTION_MAX_LENGTH}
                    helperText={
                      description.length > DESCRIPTION_MAX_LENGTH
                        ? `Maximum length is ${DESCRIPTION_MAX_LENGTH} characters.`
                        : ""
                    }
                    onChange={(
                      event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
                    ) => {
                      const { value } = event.target;
                      if (value.length > DESCRIPTION_MAX_LENGTH) return;
                      setDescription(value);
                    }}
                    inputProps={{ maxLength: DESCRIPTION_MAX_LENGTH }}
                    multiline
                    minRows={1}
                    maxRows={1}
                    sx={{
                      width: "100% !important",
                      backgroundColor: "#efe9e7 !important",
                      borderRadius: "5px",
                      "& fieldset": {
                        border: "none",
                        outline: "none",
                      },
                      "& .MuiOutlinedInput-root": {
                        padding: "0px !important",
                      },
                      "& .MuiInputBase-inputMultiline": {
                        padding: "10.5px !important",
                        fontSize: "16px",
                        fontWeight: 400,
                        overflowY: "auto !important",
                      },
                      "& textarea::placeholder": {
                        color: "#A29D9B",
                        opacity: 1,
                        fontSize: "16px",
                        fontWeight: 500,
                      },
                    }}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          {/* --- FOOTER  --- */}
          <Grid
            container
            direction="row"
            justifyContent="center"
            alignItems="center"
            className="modal-footer"
            sx={{ flexShrink: 0 }} // 
          >
            <Grid item xs={12} className="confirm-btn-container">
              <div onClick={handleClose} className="cancel-link">
                Cancel
              </div>
              <Button
                onClick={handleSubmit}
                className="confirm-btn"
                style={{ width: 156 }}
                disabled={loading}
              >
                Save
              </Button>
            </Grid>
          </Grid>
        </div>
        
        <Backdrop open={loading} style={{ zIndex: 9999, color: "#fff" }}>
          <CircularProgress />
        </Backdrop>
      </div>
    </MuiModal>
  );
};

export default AddSupplyRequestModal;
